import React, { useMemo } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { Thermometer, Droplets, Sun, CloudRain, Sparkles, TrendingDown, Gauge, Droplet } from 'lucide-react';
import { InfoTooltipButton } from './InfoTooltipButton';
import './ClimateTab.css';

// Statistics calculation helper
function calcStats(data, field) {
  const values = data.map(d => d[field]).filter(v => v != null && !isNaN(v));
  if (values.length === 0) return { mean: 0, min: 0, max: 0, stdDev: 0, n: 0 };
  
  const n = values.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);
  
  return { mean, min, max, stdDev, n };
}

export function ClimateTab() {
  const { hourlyData, timeRange } = useAnalytics();
  const { t } = useTranslation();

  // 1. Butterfly Chart Data (last 24h, hourly sampling)
  const butterflyData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];
    
    // Get last 96 points (24 hours at 15-min intervals)
    // Sample every 4th point to get hourly data
    const last96Campo = campo.slice(-96);
    const last96Retro = retro.slice(-96);
    
    const hourlyPoints = [];
    for (let i = 0; i < last96Campo.length; i += 4) {
      const c = last96Campo[i];
      const r = last96Retro[i] || {};
      hourlyPoints.push({
        hour: format(parseISO(c.ts_iso), 'HH:mm'),
        campo_temp: c.t_c || 0,
        retro_temp: r.t_c || 0,
      });
    }
    
    return hourlyPoints;
  }, [hourlyData]);

  // 2. VPD Data (last 7 days, daylight hours only)
  const vpdData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];
    
    // Get last 168 hours (7 days)
    const last168Campo = campo.slice(-168);
    const last168Retro = retro.slice(-168);
    
    // Filter daylight hours (6-20)
    const daylightData = last168Campo
      .map((c, i) => {
        const r = last168Retro[i] || {};
        const hour = parseISO(c.ts_iso).getHours();
        
        if (hour >= 6 && hour <= 20) {
          return {
            time: c.ts_iso,
            campo_vpd: c.vpd_kpa || 0,
            retro_vpd: r.vpd_kpa || 0,
          };
        }
        return null;
      })
      .filter(Boolean);
    
    return daylightData;
  }, [hourlyData]);

  // 3. Cooling Hours Calculation (last 7 days)
  const coolingStats = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) {
      return {
        total: 0,
        percentage: '0',
        avgCooling: '0.0',
        avgVpdReduction: '0.00',
        peakCoolingHour: '14:00',
        humidHours: 0,
      };
    }
    
    const last168Campo = campo.slice(-168);
    const last168Retro = retro.slice(-168);
    
    let coolingHours = 0;
    let tempSum = 0;
    let vpdSum = 0;
    let peakCoolingHour = 14;
    let maxCooling = 0;
    let humidHours = 0;
    
    last168Campo.forEach((c, i) => {
      const r = last168Retro[i];
      if (!r) return;
      
      const tempDelta = (c.t_c || 0) - (r.t_c || 0);
      const vpdDelta = (c.vpd_kpa || 0) - (r.vpd_kpa || 0);
      
      // Cooling hour = Retro is cooler AND has lower VPD
      if (r.t_c < c.t_c && r.vpd_kpa < c.vpd_kpa) {
        coolingHours++;
        tempSum += tempDelta;
        vpdSum += vpdDelta;
        
        // Track peak cooling hour
        if (tempDelta > maxCooling) {
          maxCooling = tempDelta;
          peakCoolingHour = parseISO(c.ts_iso).getHours();
        }
      }
      
      // High humidity hours in Retro
      if ((r.rh_pct || 0) > 70) humidHours++;
    });
    
    const totalHours = last168Campo.length;
    const percentage = totalHours > 0 ? (coolingHours / totalHours) * 100 : 0;
    
    return {
      total: coolingHours,
      percentage: percentage.toFixed(0),
      avgCooling: coolingHours > 0 ? (tempSum / coolingHours).toFixed(1) : '0.0',
      avgVpdReduction: coolingHours > 0 ? (vpdSum / coolingHours).toFixed(2) : '0.00',
      peakCoolingHour: `${peakCoolingHour.toString().padStart(2, '0')}:00`,
      humidHours,
    };
  }, [hourlyData]);

  // 4. Humidity Data
  const humidityData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];

    const step = Math.max(1, Math.floor(campo.length / 200));
    
    return campo.filter((_, i) => i % step === 0).map((c, idx) => {
      const r = retro[idx * step] || {};
      return {
        time: c.ts_iso,
        campo_rh: c.rh_pct || 0,
        retro_rh: r.rh_pct || 0,
      };
    });
  }, [hourlyData]);

  // 5. Pressure Data
  const pressureData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    
    if (campo.length === 0) return [];

    const step = Math.max(1, Math.floor(campo.length / 150));
    
    return campo.filter((_, i) => i % step === 0).map(c => ({
      time: c.ts_iso,
      pressure: c.pressure_hpa || 1013,
    }));
  }, [hourlyData]);

  // Climate statistics
  const climateStats = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return null;

    return {
      temp: {
        campo: calcStats(campo, 't_c'),
        retro: calcStats(retro, 't_c'),
      },
      humidity: {
        campo: calcStats(campo, 'rh_pct'),
        retro: calcStats(retro, 'rh_pct'),
      },
      vpd: {
        campo: calcStats(campo, 'vpd_kpa'),
        retro: calcStats(retro, 'vpd_kpa'),
      },
      pressure: calcStats(campo, 'pressure_hpa'),
    };
  }, [hourlyData]);

  // X-axis formatter
  const getXAxisFormat = () => {
    const days = timeRange?.days || 14;
    if (days <= 1) return 'HH:mm';
    if (days <= 7) return 'EEE HH:mm';
    return 'MMM dd';
  };

  const xAxisFormat = getXAxisFormat();

  return (
    <div className="climate-tab-container">
      {/* 1. Temperature Butterfly Chart */}
      <div className="climate-butterfly-chart">
        <div className="climate-chart-header">
          <div className="climate-chart-title-group">
            <Thermometer size={20} color="#FF6B6B" />
            <h2 className="climate-chart-title">
              {t('analytics.climate.temperatureComparison.title')}
            </h2>
          </div>
          <InfoTooltipButton
            content={t('analytics.climate.temperatureComparison.tooltip')}
          />
        </div>
        
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart
            data={butterflyData}
            margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
            
            <XAxis 
              dataKey="hour"
              stroke="#6B6B6B" 
              tick={{ fontSize: 11 }}
              label={{ value: t('analytics.climate.axes.timeHour'), position: 'insideBottom', offset: -5, style: { fontSize: 11 } }}
            />
            
            <YAxis
              stroke="#6B6B6B"
              tick={{ fontSize: 11 }}
              label={{ value: t('analytics.climate.axes.temperatureC'), angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
              domain={[0, 40]}
            />
            
            <Tooltip wrapperStyle={{ zIndex: 9999, transition: 'none' }} isAnimationActive={false} />
            
            <Legend />
            
            <Area
              type="monotone"
              dataKey="campo_temp"
              name={t('analytics.labels.field')}
              fill="#4A7A8A"
              stroke="#4A7A8A"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            
            <Area
              type="monotone"
              dataKey="retro_temp"
              name={t('analytics.labels.underPanel')}
              fill="#5A7A3A"
              stroke="#5A7A3A"
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* 2. VPD Chart - Full Width */}
      <div className="climate-vpd-chart">
        <div className="climate-chart-header">
          <div className="climate-chart-title-group">
            <Droplets size={20} color="#5A7A3A" />
            <h2 className="climate-chart-title">
              {t('analytics.climate.vpd.title')}
            </h2>
          </div>
          <InfoTooltipButton
            content={t('analytics.climate.vpd.tooltip')}
          />
        </div>
        
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={vpdData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
            
            <XAxis
              dataKey="time"
              stroke="#6B6B6B"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
            />
            
            <YAxis
              stroke="#6B6B6B"
              tick={{ fontSize: 12 }}
              label={{ value: t('analytics.climate.axes.vpdKpa'), angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              domain={[0, 3.5]}
            />
            
            <Tooltip wrapperStyle={{ zIndex: 9999, transition: 'none' }} isAnimationActive={false} />
            
            <Legend iconType="line" wrapperStyle={{ fontSize: '12px' }} />
            
            {/* Reference lines */}
            <ReferenceLine y={0.5} stroke="#8A4A3A" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.climate.vpd.reference.tooLow'), fontSize: 10 }} />
            <ReferenceLine y={1.0} stroke="#5A7A3A" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.climate.vpd.reference.optimal'), fontSize: 10 }} />
            <ReferenceLine y={2.5} stroke="#FFA500" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.climate.vpd.reference.stress'), fontSize: 10 }} />
            
            <Area
              type="monotone"
              dataKey="campo_vpd"
              name={t('analytics.climate.series.fieldVpd')}
              stroke="#4A7A8A"
              strokeWidth={2}
              fill="#4A7A8A"
              fillOpacity={0.1}
            />
            
            <Area
              type="monotone"
              dataKey="retro_vpd"
              name={t('analytics.climate.series.underVpd')}
              stroke="#5A7A3A"
              strokeWidth={2}
              fill="#5A7A3A"
              fillOpacity={0.1}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* 3. Cooling Hours Counter - Full Width */}
      <div className="climate-cooling-counter-full">
        <div className="cooling-counter-header">
          <div className="cooling-counter-header-left">
            <Sparkles size={18} color="#5A7A3A" />
            <h3 className="climate-chart-title">
              {t('analytics.climate.coolingHours.title')}
            </h3>
          </div>
          <InfoTooltipButton
            content={t('analytics.climate.coolingHours.tooltip')}
          />
        </div>
        
        <div className="cooling-counter__main">
          <div className="cooling-counter__label">
            {t('analytics.climate.coolingHours.beneficialLabel')}
          </div>
          <div className="cooling-counter__value">
            {coolingStats.total}
          </div>
          <div className="cooling-counter__percentage">
            {t('analytics.climate.coolingHours.percentOfTime', { percent: coolingStats.percentage })}
          </div>
        </div>
        
        <div className="cooling-counter__progress">
          <div 
            className="cooling-counter__progress-fill"
            style={{ width: `${coolingStats.percentage}%` }}
          />
        </div>
        
        <div className="cooling-counter__submetrics">
          <div className="submetric-card">
            <Thermometer className="submetric-card__icon" color="#4A7A8A" size={24} />
            <div className="submetric-card__value">
              -{coolingStats.avgCooling}°C
            </div>
            <div className="submetric-card__label">
              {t('analytics.climate.coolingHours.avgCooling')}
            </div>
            <div className="submetric-card__trend">
              <TrendingDown size={12} color="#5A7A3A" />
              <span style={{ color: '#5A7A3A' }}>{t('analytics.climate.coolingHours.coolerBy')}</span>
            </div>
          </div>
          
          <div className="submetric-card">
            <Droplets className="submetric-card__icon" color="#5A7A3A" size={24} />
            <div className="submetric-card__value">
              -{coolingStats.avgVpdReduction} kPa
            </div>
            <div className="submetric-card__label">
              {t('analytics.climate.coolingHours.vpdReduction')}
            </div>
            <div className="submetric-card__trend">
              <TrendingDown size={12} color="#5A7A3A" />
              <span style={{ color: '#5A7A3A' }}>{t('analytics.climate.coolingHours.lessStress')}</span>
            </div>
          </div>
          
          <div className="submetric-card">
            <Sun className="submetric-card__icon" color="#FFA500" size={24} />
            <div className="submetric-card__value">
              {coolingStats.peakCoolingHour}
            </div>
            <div className="submetric-card__label">
              {t('analytics.climate.coolingHours.peakCooling')}
            </div>
            <div className="submetric-card__trend">
              <span style={{ color: '#6B6B6B' }}>{t('analytics.climate.coolingHours.hottestHour')}</span>
            </div>
          </div>
          
          <div className="submetric-card">
            <CloudRain className="submetric-card__icon" color="#6B6B6B" size={24} />
            <div className="submetric-card__value">
              {coolingStats.humidHours}h
            </div>
            <div className="submetric-card__label">
              {t('analytics.climate.coolingHours.humidHours')}
            </div>
            {coolingStats.humidHours > 80 ? (
              <div className="submetric-card__trend" style={{ color: '#FFA500' }}>
                {t('analytics.climate.coolingHours.monitorRisk')}
              </div>
            ) : (
              <div className="submetric-card__trend" style={{ color: '#5A7A3A' }}>
                {t('analytics.climate.coolingHours.normal')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 4. Humidity Chart */}
      <div className="climate-humidity-chart">
        <div className="climate-chart-header">
          <div className="climate-chart-title-group">
            <Droplet size={20} color="#4A7A8A" />
            <h2 className="climate-chart-title">
              {t('analytics.climate.humidity.title')}
            </h2>
          </div>
          <InfoTooltipButton content={t('analytics.climate.humidity.tooltip')} />
        </div>
        
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={humidityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
            
            <XAxis
              dataKey="time"
              stroke="#6B6B6B"
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => format(parseISO(value), xAxisFormat)}
            />
            
            <YAxis
              stroke="#6B6B6B"
              tick={{ fontSize: 11 }}
              label={{ value: t('analytics.climate.axes.rhPercent'), angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
              domain={[20, 100]}
            />
            
            <Tooltip
              isAnimationActive={false}
              wrapperStyle={{ transition: 'none' }}
              formatter={(value) => [`${value.toFixed(1)}%`, '']}
              labelFormatter={(label) => format(parseISO(label), 'MMM dd, HH:mm')}
            />
            
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            
            {/* Reference lines for optimal range */}
            <ReferenceLine y={50} stroke="#5A7A3A" strokeDasharray="5 5" strokeWidth={1} />
            <ReferenceLine y={70} stroke="#5A7A3A" strokeDasharray="5 5" strokeWidth={1} />
            
            <Area
              type="monotone"
              dataKey="retro_rh"
              name={t('analytics.climate.series.underHumidity')}
              stroke="#5A7A3A"
              strokeWidth={2}
              fill="#5A7A3A"
              fillOpacity={0.2}
            />
            
            <Area
              type="monotone"
              dataKey="campo_rh"
              name={t('analytics.climate.series.fieldHumidity')}
              stroke="#4A7A8A"
              strokeWidth={2}
              fill="#4A7A8A"
              fillOpacity={0.2}
            />
          </AreaChart>
        </ResponsiveContainer>
        
        <div className="climate-chart-interpretation">
          <strong>{t('analytics.common.interpretationLabel')}:</strong>{' '}
          {t('analytics.climate.humidity.interpretation')}
        </div>
      </div>

      {/* 6. Pressure Chart */}
      <div className="climate-pressure-chart">
        <div className="climate-chart-header">
          <div className="climate-chart-title-group">
            <Gauge size={20} color="#6B6B6B" />
            <h2 className="climate-chart-title">
              {t('analytics.climate.pressure.title')}
            </h2>
          </div>
          <InfoTooltipButton content={t('analytics.climate.pressure.tooltip')} />
        </div>
        
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={pressureData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
            
            <XAxis
              dataKey="time"
              stroke="#6B6B6B"
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => format(parseISO(value), xAxisFormat)}
            />
            
            <YAxis
              stroke="#6B6B6B"
              tick={{ fontSize: 11 }}
              label={{ value: 'hPa', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
              domain={['dataMin - 5', 'dataMax + 5']}
            />
            
            <Tooltip
              isAnimationActive={false}
              wrapperStyle={{ transition: 'none' }}
              formatter={(value) => [`${value.toFixed(1)} hPa`, t('analytics.metrics.pressure')]}
              labelFormatter={(label) => format(parseISO(label), 'MMM dd, HH:mm')}
            />
            
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            
            {/* Standard pressure reference */}
            <ReferenceLine y={1013.25} stroke="#333" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.climate.pressure.standardLabel'), position: 'insideTopRight', fontSize: 10 }} />
            
            <Line
              type="monotone"
              dataKey="pressure"
              name={t('analytics.metrics.pressure')}
              stroke="#6B6B6B"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
        
        <div className="climate-chart-interpretation">
          <strong>{t('analytics.common.interpretationLabel')}:</strong>{' '}
          {t('analytics.climate.pressure.interpretation')}
        </div>
      </div>

      {/* Statistics Summary Table */}
      {climateStats && (
        <div className="climate-stats-section">
          <div className="climate-chart-header">
            <div className="climate-chart-title-group">
              <Thermometer size={20} color="#4A7A8A" />
              <h2 className="climate-chart-title">
                {t('analytics.statsTable.title')}
              </h2>
            </div>
            <InfoTooltipButton content={t('analytics.climate.stats.tooltip')} />
          </div>
          
          <div className="statistics-table-container">
            <table className="statistics-table">
              <thead>
                <tr>
                  <th>{t('analytics.statsTable.metric')}</th>
                  <th>{t('analytics.statsTable.fieldMeanSd')}</th>
                  <th>{t('analytics.statsTable.underMeanSd')}</th>
                  <th>{t('analytics.statsTable.deltaDifference')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('analytics.statsTable.temperatureWithUnit')}</td>
                  <td>{climateStats.temp.campo.mean.toFixed(1)} ± {climateStats.temp.campo.stdDev.toFixed(1)}</td>
                  <td>{climateStats.temp.retro.mean.toFixed(1)} ± {climateStats.temp.retro.stdDev.toFixed(1)}</td>
                  <td className={climateStats.temp.retro.mean < climateStats.temp.campo.mean ? 'positive' : 'negative'}>
                    {(climateStats.temp.retro.mean - climateStats.temp.campo.mean).toFixed(1)}°C
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.statsTable.humidityWithUnit')}</td>
                  <td>{climateStats.humidity.campo.mean.toFixed(1)} ± {climateStats.humidity.campo.stdDev.toFixed(1)}</td>
                  <td>{climateStats.humidity.retro.mean.toFixed(1)} ± {climateStats.humidity.retro.stdDev.toFixed(1)}</td>
                  <td className={climateStats.humidity.retro.mean > climateStats.humidity.campo.mean ? 'positive' : 'negative'}>
                    {climateStats.humidity.retro.mean > climateStats.humidity.campo.mean ? '+' : ''}{(climateStats.humidity.retro.mean - climateStats.humidity.campo.mean).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.statsTable.vpdWithUnit')}</td>
                  <td>{climateStats.vpd.campo.mean.toFixed(2)} ± {climateStats.vpd.campo.stdDev.toFixed(2)}</td>
                  <td>{climateStats.vpd.retro.mean.toFixed(2)} ± {climateStats.vpd.retro.stdDev.toFixed(2)}</td>
                  <td className={climateStats.vpd.retro.mean < climateStats.vpd.campo.mean ? 'positive' : 'negative'}>
                    {(climateStats.vpd.retro.mean - climateStats.vpd.campo.mean).toFixed(2)} kPa
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.statsTable.pressureWithUnit')}</td>
                  <td colSpan="2" style={{ textAlign: 'center' }}>{climateStats.pressure.mean.toFixed(1)} ± {climateStats.pressure.stdDev.toFixed(1)}</td>
                  <td>{t('analytics.statsTable.sameAtBoth')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
